import React, { useEffect, useState, useCallback } from "react";
import * as A from "./lib/api";
import { H1, H2, Sub, Card } from "./Pages";
import { toast } from "sonner";
import { Landmark, Building2, HeartHandshake, Shield, Network, Plus, Trash2, AlertTriangle, HelpCircle, Lightbulb } from "lucide-react";

const STATUSES = ["Verified","Reported","Proposed","Disputed","Conflicting Documentation","Awaiting Documentation","Professional Review Required","Superseded"];
const statusTone = (s) => {
  if (s === "Verified") return "green";
  if (s === "Disputed" || s === "Conflicting Documentation") return "red";
  if (s === "Professional Review Required") return "gold";
  if (s === "Superseded") return "gray";
  return "amber";
};
const chip = (s) => <span className={`chip chip-${statusTone(s)} text-xs`}>{s || "Awaiting Documentation"}</span>;

// Field definitions per record type (kept structural — no fabricated legal data)
const TABS = {
  apex_trusts: { label: "Dynasty Trusts", icon: Landmark, fields: ["DisplayName","LegalName","Type","GoverningLaw","Situs","HoldingInterestPercent","Source","Notes"] },
  apex_holding: { label: "Holding LLC", icon: Building2, single: true, fields: ["DisplayName","LegalName","LegalForm","FederalTaxClassification","StateTaxClassification","Source","Notes"] },
  apex_subsidiaries: { label: "Subsidiaries", icon: Network, fields: ["DisplayName","LegalName","Type","Jurisdiction","DirectOwner","OwnershipPercent","TaxTreatment","Source","Notes"] },
  apex_nonprofit: { label: "Nonprofit", icon: HeartHandshake, single: true, fields: ["DisplayName","LegalName","LegalForm","RelationshipLabel","Source","Notes"] },
  apex_purpose_trust: { label: "Purpose Trust", icon: Shield, single: true, fields: ["DisplayName","LegalName","Purpose","GoverningLaw","Situs","Source","Notes"] },
  apex_guardian_share: { label: "Guardian Share", icon: Shield, single: true, fields: ["DisplayName","Class","VotingRights","ConsentRights","VetoRights","InformationRights","EconomicRights","Source","Notes"] },
  apex_ownership: { label: "Ownership", icon: Network, fields: ["Owner","Entity","InterestType","Percent","Source","Notes"], ownership: true },
};

export function ApexStructurePage() {
  const [tab, setTab] = useState("apex_trusts");
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState(null);
  const [editing, setEditing] = useState(null); // record or {} for new

  const load = useCallback(async () => {
    const [r, s] = await Promise.all([A.reg.list(tab), A.apexSummary()]);
    setRows(r.data); setSummary(s.data);
  }, [tab]);
  useEffect(() => { load(); }, [load]);

  const def = TABS[tab];
  const startNew = () => { const o = {}; def.fields.forEach(f => o[f] = ""); o.Status = "Awaiting Documentation"; setEditing(o); };
  const save = async () => {
    try {
      const payload = { ...editing };
      if (payload.id) { const id = payload.id; delete payload.id; await A.reg.patch(tab, id, payload); }
      else await A.reg.create(tab, payload);
      toast.success("Saved"); setEditing(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
  };
  const del = async (id) => { if (window.confirm("Delete this record?")) { await A.reg.del(tab, id); load(); } };

  return (
    <div data-testid="apex-structure-page">
      <H1>Apex Structure</H1>
      <Sub>Connected but legally distinct records — five Dynasty Trusts, the Holding LLC, subsidiaries, the associated (separate) nonprofit, and the Purpose Trust holding the Guardian Share. Ownership, voting, economics and control are tracked separately and never collapsed. Nothing here is legal advice; unverified items stay flagged.</Sub>

      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
          <Card><div className="label-caps">Dynasty Trusts</div><div className="font-display text-3xl mt-1" style={{ color: 'var(--primary)' }}>{summary.trusts}</div></Card>
          <Card><div className="label-caps">Subsidiaries</div><div className="font-display text-3xl mt-1" style={{ color: 'var(--primary)' }}>{summary.subsidiaries}</div></Card>
          <Card><div className="label-caps">Ownership records</div><div className="font-display text-3xl mt-1" style={{ color: 'var(--primary)' }}>{summary.ownership_records}</div></Card>
          <Card><div className="label-caps">Guardian Share</div><div className="mt-2">{summary.guardian_share_present ? chip("Professional Review Required") : chip("Awaiting Documentation")}</div></Card>
        </div>
      )}

      {summary?.validation_flags?.length > 0 && (
        <div className="banner-warning mb-6" data-testid="apex-flags">
          <div className="flex items-center gap-2 font-medium mb-1"><AlertTriangle size={15} /> Source & validation flags</div>
          <ul className="text-sm list-disc ml-5">{summary.validation_flags.map((f, i) => <li key={`flag-${i}`}>{f}</li>)}</ul>
        </div>
      )}

      <div className="flex flex-wrap gap-2 mb-4">
        {Object.entries(TABS).map(([k, d]) => (
          <button key={k} onClick={() => { setTab(k); setEditing(null); }} className={tab === k ? "btn-primary text-xs" : "btn-outline text-xs"} data-testid={`apex-tab-${k}`}>
            <d.icon size={13} className="inline mr-1" />{d.label}
          </button>
        ))}
      </div>

      <div className="flex justify-between items-center mb-3">
        <H2>{def.label}</H2>
        {!(def.single && rows.length >= 1) && <button className="btn-gold flex items-center gap-1" onClick={startNew} data-testid="apex-new"><Plus size={14} /> Add record</button>}
      </div>

      {editing && (
        <Card className="mb-4" data-testid="apex-edit-form">
          <div className="grid md:grid-cols-2 gap-3">
            {def.fields.map(f => (
              <div key={f}>
                <label className="label-caps">{f}</label>
                {f === "Notes" ? <textarea value={editing[f] || ""} onChange={e => setEditing({ ...editing, [f]: e.target.value })} rows={2} data-testid={`apex-field-${f}`} />
                  : <input value={editing[f] || ""} onChange={e => setEditing({ ...editing, [f]: e.target.value })} data-testid={`apex-field-${f}`} />}
              </div>
            ))}
            <div>
              <label className="label-caps">Status</label>
              <select value={editing.Status || "Awaiting Documentation"} onChange={e => setEditing({ ...editing, Status: e.target.value })} data-testid="apex-field-Status">
                {STATUSES.map(s => <option key={s}>{s}</option>)}
              </select>
            </div>
          </div>
          <div className="flex gap-2 mt-3">
            <button className="btn-primary" onClick={save} data-testid="apex-save">Save</button>
            <button className="btn-outline" onClick={() => setEditing(null)}>Cancel</button>
          </div>
        </Card>
      )}

      <div className="space-y-2">
        {rows.length === 0 ? <Card><div className="text-sm" style={{ color: 'var(--muted)' }}>No records yet.</div></Card> : rows.map(r => (
          <Card key={r.id} data-testid={`apex-row-${r.id}`}>
            <div className="flex justify-between items-start gap-3">
              <div className="min-w-0">
                <div className="font-medium" style={{ color: 'var(--primary)' }}>
                  {r.DisplayName || r.Owner || r.LegalName || "Untitled"} {chip(r.Status)}
                </div>
                <div className="text-xs mt-1" style={{ color: 'var(--muted)' }}>
                  {def.fields.filter(f => !["DisplayName","Notes"].includes(f) && r[f]).map(f => `${f}: ${r[f]}`).join(" · ") || "Awaiting Trustee Input"}
                </div>
                {r.Notes && <div className="text-xs mt-1" style={{ color: 'var(--muted)' }}>{r.Notes}</div>}
              </div>
              <div className="flex gap-1 shrink-0">
                <button className="btn-outline text-xs" onClick={() => setEditing(r)} data-testid={`apex-edit-${r.id}`}>Edit</button>
                <button className="btn-outline text-xs" style={{ color: 'var(--overdue)' }} onClick={() => del(r.id)} data-testid={`apex-del-${r.id}`}><Trash2 size={12} /></button>
              </div>
            </div>
          </Card>
        ))}
      </div>
    </div>
  );
}

// ==================== REGISTERS: Trustee Questions + Proposed Improvements (§24) ====================
export function RegistersPage() {
  const [tab, setTab] = useState("trustee_questions");
  const [rows, setRows] = useState([]);
  const load = useCallback(async () => { const r = await A.reg.list(tab); setRows(r.data); }, [tab]);
  useEffect(() => { load(); }, [load]);

  const answer = async (r) => {
    const a = prompt("Trustee answer / decision:", r.Answer || "");
    if (a === null) return;
    await A.reg.patch("trustee_questions", r.id, { Answer: a, Status: a ? "Answered" : "Awaiting Trustee Input" });
    toast.success("Recorded"); load();
  };

  return (
    <div data-testid="registers-page">
      <H1>Governance Registers</H1>
      <Sub>Open questions requiring Trustee Smith's decision, and improvements proposed by the build that await approval. Neither halts approved work; nothing is treated as approved by silence.</Sub>
      <div className="flex gap-2 mb-4">
        <button className={tab === "trustee_questions" ? "btn-primary text-xs" : "btn-outline text-xs"} onClick={() => setTab("trustee_questions")} data-testid="reg-tab-questions"><HelpCircle size={13} className="inline mr-1" />Trustee Questions</button>
        <button className={tab === "proposed_improvements" ? "btn-primary text-xs" : "btn-outline text-xs"} onClick={() => setTab("proposed_improvements")} data-testid="reg-tab-improvements"><Lightbulb size={13} className="inline mr-1" />Proposed Improvements</button>
      </div>

      {tab === "trustee_questions" ? (
        <div className="space-y-2">
          {rows.map(q => (
            <Card key={q.id} data-testid={`question-${q.id}`}>
              <div className="flex justify-between items-start gap-3">
                <div>
                  <div className="text-xs mb-1"><span className="chip chip-gold text-xs">{q.Group}</span> {q.Status === "Answered" ? <span className="chip chip-green text-xs">Answered</span> : <span className="chip chip-amber text-xs">Awaiting Trustee Input</span>}</div>
                  <div className="text-sm">{q.Question}</div>
                  {q.Answer && <div className="text-sm mt-1" style={{ color: 'var(--primary)' }}>Answer: {q.Answer}</div>}
                </div>
                <button className="btn-outline text-xs shrink-0" onClick={() => answer(q)} data-testid={`answer-${q.id}`}>Answer</button>
              </div>
            </Card>
          ))}
        </div>
      ) : (
        <div className="space-y-2">
          {rows.map(p => (
            <Card key={p.id} data-testid={`improvement-${p.id}`}>
              <div className="font-medium" style={{ color: 'var(--primary)' }}>{p.Name} <span className="chip chip-amber text-xs ml-1">{p.Status}</span></div>
              <div className="text-sm mt-1">{p.ProblemAddressed}</div>
              <div className="text-xs mt-1" style={{ color: 'var(--muted)' }}>Benefit: {p.Benefit} · Approval required: {p.ApprovalRequired}</div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
